// The "AI-built sources" tour — explains the agentic source family (External
// API / Web Scraper / AI Enrichment / News & Feeds) and the MASTER flow that
// creates them (plain-language brief → live-verified config). Global: every
// project has the create modal, so this tour is offered everywhere, after the
// core product tour. Declarative only — the tutorial overlay blocks page
// interaction, so the create modal itself is explained with centered cards
// rather than opened mid-tour.

export const agenticSourcesTour = {
  key: "agentic-sources",
  title: "AI-built sources",
  description:
    "How to add data without hand-typing connections: describe what you need and a master builds, live-verifies and configures the source — External API, Web Scraper, AI Enrichment, News & Feeds, Knowledge Base (RAG).",
  steps: [
    {
      title: "Two ways to add data",
      body:
        "Classic <b>connectors</b> (Postgres, files, warehouses…) ask you for hosts, credentials " +
        "and table names — you know the connection, you type it in.<br/><br/>" +
        "<b>AI-built sources</b> flip that around: you describe <i>what data you need</i> in plain " +
        "language, and a <b>master</b> — a specialised build assistant — works out the configuration, " +
        "<b>tests it live</b>, shows you real sample rows, and only then creates the source.<br/><br/>" +
        "There are five AI-built families: <b>External API</b>, <b>Web Scraper</b>, " +
        "<b>AI Enrichment</b>, <b>News & Feeds</b> and <b>Knowledge Base (RAG)</b>. This tour explains each one.",
    },
    {
      page: "data-sources",
      target: '[data-tour="page-data-sources"], main',
      title: "Sources — one list, two families",
      body:
        "Every source lands here, whatever its family. AI-built sources are marked with a violet " +
        "<b>✨ AI-built</b> badge so you can always tell them apart from hand-configured connectors. " +
        "Like any source, they start as <b>Draft</b> and become <b>Active</b> once they have field " +
        "mappings.",
    },
    {
      // Array form = PRIORITY order (a comma selector resolves in document
      // order, and `main` precedes the button — it would swallow the spotlight).
      target: ['[data-tour="new-source"]', 'main'],
      title: "Creating one starts here",
      body:
        "Click <b>New Source</b> → <b>Manual</b> to open the create form. Let's open it for real " +
        "and walk through it (sample values only — the tour never creates anything).",
      placement: "bottom",
    },
    {
      demo: "new-source",
      target: '[data-testid="source-type-trigger"], [data-tutorial-demo-modal]',
      title: "The create form — pick a type",
      body:
        "This is the actual create form. Name the source, then pick its <b>Type</b> (spotlighted): " +
        "the dropdown groups plain <b>Connectors</b> (files, Postgres, Mongo, …) on top and the " +
        "<b>✨ AI-built sources</b> group below with the four master-built types.<br/><br/>" +
        "A plain connector shows its connection fields (host, credentials, table…); a file source " +
        "is created metadata-first and gets its file uploaded on the detail page afterwards.",
    },
    {
      demo: "new-source-agentic",
      target: '[data-testid="master-panel"], [data-tutorial-demo-modal]',
      title: "The master panel",
      body:
        "Picking an AI-built type swaps the connection form for the <b>master panel</b> " +
        "(spotlighted): 1–3 plain-language brief fields instead of connection details, and a " +
        "<b>Build</b> button that makes the master derive, live-probe and verify the " +
        "configuration before anything is created.",
    },
    {
      title: "How a master works",
      body:
        "Every master follows the same three beats:<br/><br/>" +
        "1. <b>Brief</b> — you fill 1–3 simple fields (what you need, maybe a URL or a topic).<br/>" +
        "2. <b>Build</b> — the master derives a configuration and <b>probes it for real</b>: it calls " +
        "the API, reads the feeds, dry-runs the scrape or enrichment. If something doesn't work it " +
        "repairs and retries once, then tells you honestly.<br/>" +
        "3. <b>Verified preview</b> — you see the discovered <b>schema</b>, live <b>sample rows</b> " +
        "and the master's notes. Only when you like what you see do you click <b>Create Source</b>." +
        "<br/><br/>Nothing is created until it has been proven to work.",
    },
    {
      title: "External API — any JSON API",
      body:
        "Use it for REST APIs — public or internal.<br/><br/><b>Brief:</b> describe the data " +
        "(<i>“German public holidays for 2026”</i>), paste the URL if you know it, and add an API " +
        "token if one is needed.<br/><br/><b>The master:</b> calls the API, finds <i>where the " +
        "records live</i> in the response (the records path) and infers the column schema from real " +
        "rows. If you gave no URL it proposes a well-known public API and verifies it before " +
        "suggesting it.",
    },
    {
      title: "Web Scraper — organizations or any web page",
      body:
        "Two modes, chosen automatically from your brief:<br/><br/>" +
        "<b>Organizations</b> — <i>“veterinary clinics in Warsaw”</i>: searches real-world " +
        "organizations by type + location and returns uniform rows (name, website, phone, address…)." +
        "<br/><br/><b>Web pages</b> — paste one or more page URLs and say what to extract " +
        "(<i>“talks on this schedule page: title, speaker, time”</i>): the master reads the pages and " +
        "extracts exactly those fields as rows.<br/><br/>Either way it <b>test-scrapes before " +
        "creating</b> — you see real results in the preview.",
    },
    {
      title: "AI Enrichment — add fields to an existing source",
      body:
        "Enrichment doesn't fetch new records — it <b>adds columns to records you already have</b>." +
        "<br/><br/><b>Brief:</b> pick the upstream source from a dropdown and say what to add " +
        "(<i>“describe what each company does and how well our product fits”</i>).<br/><br/>" +
        "<b>The master:</b> reads a few upstream records, writes the per-record enrichment prompt and " +
        "field names, and <b>dry-runs one real record</b> so the preview shows an actually-enriched " +
        "row.<br/><br/>Every enriched fact carries a <b>confidence score</b> and is flagged " +
        "<code>enrichment_confirmed=false</code> until a human approves it — enriched data never " +
        "silently masquerades as ground truth.",
    },
    {
      title: "News & Feeds — follow any topic",
      body:
        "Turn news monitoring into a data source.<br/><br/><b>Brief:</b> name what you want to follow " +
        "(<i>“EU medical-device regulation”</i>, <i>“releases of the Kubernetes project”</i>), or " +
        "paste feed URLs you already know. Optionally add a keyword filter.<br/><br/>" +
        "<b>The master:</b> finds candidate RSS/Atom feeds for the topic, <b>fetches each one</b> and " +
        "keeps only the feeds that actually respond with items. Every read then returns uniform rows " +
        "— title, link, published date, summary, source — ready to map and pipe like any table.",
    },
    {
      title: "Knowledge Base (RAG) — chat with your documents",
      body:
        "The fifth AI-built family turns a <b>document library</b> into a data source.<br/><br/>" +
        "<b>Brief:</b> say what the knowledge base is for and (optionally) name starting folders." +
        "<br/><br/><b>The master:</b> provisions a vector index for the source, then <b>proves it " +
        "live</b> — it indexes a tiny probe document, checks retrieval finds it, and cleans it up. " +
        "Only then is the source created.<br/><br/>Every project with a Knowledge Base gets a " +
        "<b>document explorer app</b> in Apps: upload files, watch them auto-index, and ask " +
        "questions in a chat that answers with <b>citations into your documents</b>. The source's " +
        "records are the governed <b>document registry</b> — what's indexed, when, and how thoroughly.",
    },
    {
      page: "data-sources",
      target: '[data-tour="page-data-sources"], main',
      title: "After creation — an ordinary source",
      body:
        "An AI-built source behaves like any other from here on. Its detail page shows the " +
        "master-built configuration on the <b>Connection</b> card (editable if you want to tweak it), " +
        "a <b>Schema</b>, and a <b>Records</b> section reading live data. Map its fields to your data " +
        "elements, wire it into the pipeline with <b>Load into target</b>, and the governed rows land " +
        "in your target with full lineage.<br/><br/>Need a different shape later? Adjust the config — " +
        "or just delete the source and brief the master again. That's the whole idea: <b>describe, " +
        "verify, govern</b>.",
    },
  ],
};
